import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import '../../css/scorecard.css'

// ----------------------------- Utilities ------------------------------ //
const ballsToOversStr = (balls = 0) => `${Math.floor(balls / 6)}.${balls % 6}`
const economy = (runs = 0, balls = 0) => {
  if (!balls) return '-'
  const overs = balls / 6
  return (runs / overs).toFixed(2)
}
const strikeRate = (runs = 0, balls = 0) => (balls ? ((runs / balls) * 100).toFixed(2) : '-')
const sumExtras = (ex = {}) =>
  (ex.b || 0) + (ex.lb || 0) + (ex.nb || 0) + (ex.wd || 0) + (ex.pn || 0)
const cls = (...a) => a.filter(Boolean).join(' ')
const decodeEntities = (html = '') => {
  if (!html) return ''
  if (typeof window !== 'undefined') {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    return doc.documentElement.textContent || ''
  }
  return html
}

// ----------------------------- Component ----------------------------- //
export default function Scorecard({ paramOrder = 'mid-cid' }) {
  const params = useParams()
  const mid = params.mid
  const cid = params.cid

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [tab, setTab] = useState(1) // 1 => innings1, 2 => innings2

  useEffect(() => {
    let isMounted = true
    const ac = new AbortController()
    const consumerKey = atob(process.env.REACT_APP_CONSUMER_KEY || '')
    const apiKey = atob(process.env.REACT_APP_API_KEY || '')

    async function run() {
      setLoading(true)
      setError('')
      try {
        const url = `https://core-prod-origin.cricclubs.com/core/scoreCard/getScoreCard?matchId=${mid}&clubId=${cid}`
        const headerBag = {
          Accept: 'application/json',
          ...(consumerKey ? { 'x-consumer-key': consumerKey } : {}),
          ...(apiKey ? { 'x-api-key': apiKey } : {})
        }
        const res = await fetch(url, { method: 'GET', headers: headerBag, signal: ac.signal })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const json = await res.json()
        if (isMounted) setData(json?.data || null)
      } catch (e) {
        if (isMounted) setError(e?.message || 'Failed to fetch scorecard')
      } finally {
        if (isMounted) setLoading(false)
      }
    }
    run()

    return () => {
      isMounted = false
      ac.abort()
    }
  }, [mid, cid])

  const match = data?.matchInfo
  const innings1 = data?.innings1 || null
  const innings2 = data?.innings2 || null

  const pageTitle = useMemo(() => {
    if (!match) return 'Scorecard'
    return `${match.teamOneName || 'Team A'} vs ${match.teamTwoName || 'Team B'} – Scorecard`
  }, [match])

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.title = pageTitle
    }
  }, [pageTitle])

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} />
  if (!data) return <ErrorState message='No data found for this scorecard.' />

  return (
    <div className='scorecard-root'>
      {/* Header */}
      <div className='container'>
        <header className='header'>
          <div className='header-left'>
            <h1 className='title'>
              <span className='team-chip team-a'>{match?.teamOneName}</span>
              <span className='vs'>vs</span>
              <span className='team-chip team-b'>{match?.teamTwoName}</span>
            </h1>
            {match?.ground && (
              <p className='meta subtle'>
                <span>Venue:</span> {match.ground}
              </p>
            )}
            {match?.matchDate && (
              <p className='meta subtle'>
                <span>Date:</span> {match.matchDate}
              </p>
            )}
          </div>
          <div className='header-right'>
            <KpiPills innings1={innings1} innings2={innings2} />
          </div>
        </header>

        {/* Tabs */}
        <div role='tablist' aria-label='Innings Tabs' className='tabs'>
          <button
            role='tab'
            aria-selected={tab === 1}
            className={cls('tab', tab === 1 && 'active')}
            onClick={() => setTab(1)}
          >
            {innings1?.teamName || match?.teamOneName} – Innings
          </button>
          <button
            role='tab'
            aria-selected={tab === 2}
            className={cls('tab', tab === 2 && 'active')}
            onClick={() => setTab(2)}
          >
            {innings2?.teamName || match?.teamTwoName} – Innings
          </button>
        </div>

        <main className='content'>
          {tab === 1 && innings1 && <InningsBlock innings={innings1} />}
          {tab === 2 && innings2 && <InningsBlock innings={innings2} />}
          {!innings1 && tab === 1 && <EmptyInnings name={match?.teamOneName} />}
          {!innings2 && tab === 2 && <EmptyInnings name={match?.teamTwoName} />}
        </main>
      </div>
    </div>
  )
}

function KpiPills({ innings1, innings2 }) {
  const topBatter = getTopBatter([...(innings1?.batting || []), ...(innings2?.batting || [])])
  const topBowler = getTopBowler([...(innings1?.bowling || []), ...(innings2?.bowling || [])])
  return (
    <div className='kpi-pills'>
      {topBatter && (
        <div className='pill pill-batter' title='Top Batter (most runs)'>
          <span className='pill-label'>Top Batter</span>
          <span className='pill-value'>
            {nameOf(topBatter)} • {topBatter.runsScored}
          </span>
        </div>
      )}
      {topBowler && (
        <div className='pill pill-bowler' title='Top Bowler (wkts, econ)'>
          <span className='pill-label'>Top Bowler</span>
          <span className='pill-value'>
            {nameOf(topBowler)} • {topBowler.wickets}/{topBowler.runs} (
            {economy(topBowler.runs, topBowler.balls)})
          </span>
        </div>
      )}
    </div>
  )
}

function nameOf(p) {
  return [p.firstName, p.lastName].filter(Boolean).join(' ')
}

function getTopBatter(players) {
  if (!Array.isArray(players) || !players.length) return null
  return players.reduce((a, b) => (Number(b.runsScored || 0) > Number(a.runsScored || 0) ? b : a))
}

function getTopBowler(players) {
  if (!Array.isArray(players) || !players.length) return null
  // prioritize wickets, then economy (lower better), then runs (lower better)
  return players.reduce((best, cur) => {
    const bw = Number(best.wickets || 0)
    const cw = Number(cur.wickets || 0)
    if (cw !== bw) return cw > bw ? cur : best
    const be = parseFloat(economy(best.runs, best.balls)) || Infinity
    const ce = parseFloat(economy(cur.runs, cur.balls)) || Infinity
    if (be !== ce) return ce < be ? cur : best
    const br = Number(best.runs || 0)
    const cr = Number(cur.runs || 0)
    return cr < br ? cur : best
  })
}

function EmptyInnings({ name }) {
  return (
    <div className='empty' role='status'>
      No innings data for <strong>{name}</strong>.
    </div>
  )
}

function LoadingState() {
  return (
    <div className='loading-root'>
      <div className='skeleton title-line' />
      <div className='skeleton tab-line' />
      <div className='skeleton card' />
    </div>
  )
}

function ErrorState({ message = 'Something went wrong.' }) {
  return (
    <div className='error-root'>
      <div className='error-card' role='alert'>
        <p className='error-text'>{message}</p>
        <p className='error-sub'>Please check the URL parameters (mid/cid) and try again.</p>
      </div>
    </div>
  )
}

function SectionCard({ title, children, className = '' }) {
  return (
    <section className={cls('card', className)}>
      <div className='card-header'>{title}</div>
      <div className='card-body'>{children}</div>
    </section>
  )
}

function InningsSummary({ innings }) {
  const extraTotal = sumExtras(innings?.extras)
  return (
    <div className='innings-summary'>
      <div className='left'>
        <h2 className='score'>
          {innings.teamName} {innings.total}/{innings.wickets}
          <span className='ov'>({innings.overs} ov)</span>
        </h2>
        <p className='rr'>
          Run Rate: <strong>{innings.runRate}</strong>
        </p>
      </div>
      <div className='right'>
        Extras: <strong>{extraTotal}</strong>
        <span className='detail'>
          (b {innings.extras?.b || 0}, lb {innings.extras?.lb || 0}, nb {innings.extras?.nb || 0},
          wd {innings.extras?.wd || 0}, pn {innings.extras?.pn || 0})
        </span>
      </div>
    </div>
  )
}

function InningsBlock({ innings }) {
  const dnbPlayers = (innings?.batting || []).filter(
    b =>
      (b?.outStringNoLink || '').toUpperCase() === 'DNB' ||
      (!b.isOut && !b.runsScored && !b.ballsFaced)
  )

  return (
    <div className='innings-block'>
      <SectionCard className='my-4' title='Score Summary'>
        <InningsSummary innings={innings} />
      </SectionCard>

      <SectionCard title='Batting'>
        <div className='table batting'>
          <div className='t-head'>
            <div>Batter</div>
            <div>R</div>
            <div>B</div>
            <div>4s</div>
            <div>6s</div>
            <div className='right'>SR</div>
          </div>
          <div className='t-body'>
            {(innings?.batting || []).map((b, idx) => (
              <div key={`${b.playerID}-${idx}`} className={cls('t-row', idx % 2 ? 'even' : 'odd')}>
                <div className='player'>
                  <img
                    src={`https://media.cricclubs.com${b.profilepic_file_path || ''}`}
                    alt={b.firstName || 'player'}
                    className='avatar'
                    onError={e => (e.currentTarget.style.visibility = 'hidden')}
                  />
                  <div className='p-meta'>
                    <div className='name'>{nameOf(b)}</div>
                    <div className='dismissal' title={decodeEntities(b.outStringNoLink)}>
                      {decodeEntities(b.outStringNickNamesNoLink || b.outStringNoLink)}
                    </div>
                  </div>
                </div>
                <div className='num emph'>{b.runsScored}</div>
                <div className='num'>{b.ballsFaced}</div>
                <div className='num'>{b.fours}</div>
                <div className='num'>{b.sixers}</div>
                <div className='num right'>{strikeRate(b.runsScored, b.ballsFaced)}</div>
              </div>
            ))}
          </div>
        </div>

        <div className='extras-total'>
          <div className='chip chip-soft'>
            <span className='label'>Extras</span>
            <span className='value'>{sumExtras(innings.extras)}</span>
            <span className='muted'>
              (b {innings.extras?.b || 0}, lb {innings.extras?.lb || 0}, nb{' '}
              {innings.extras?.nb || 0}, wd {innings.extras?.wd || 0}, pn {innings.extras?.pn || 0})
            </span>
          </div>
          <div className='chip chip-strong'>
            <span className='label'>Total</span>
            <span className='value'>
              {innings.total}/{innings.wickets}
            </span>
            <span className='muted'>in {innings.overs} ov</span>
          </div>
        </div>

        {dnbPlayers.length > 0 && (
          <div className='dnb'>
            <span className='title'>Did not bat:</span>
            <span className='list'>{dnbPlayers.map(p => nameOf(p)).join(', ')}</span>
          </div>
        )}
      </SectionCard>

      <SectionCard className='my-4' title='Bowling'>
        <div className='table bowling'>
          <div className='t-head'>
            <div>Bowler</div>
            <div>O</div>
            <div>M</div>
            <div>R</div>
            <div>W</div>
            <div className='right'>Econ</div>
          </div>
          <div className='t-body'>
            {(innings?.bowling || []).map((bo, idx) => (
              <div key={`${bo.playerID}-${idx}`} className={cls('t-row', idx % 2 ? 'even' : 'odd')}>
                <div className='player'>
                  <img
                    src={`https://media.cricclubs.com${bo.profilepic_file_path || ''}`}
                    alt={bo.firstName || 'bowler'}
                    className='avatar'
                    onError={e => (e.currentTarget.style.visibility = 'hidden')}
                  />
                  <div className='p-meta'>
                    <div className='name'>{nameOf(bo)}</div>
                    <div className='style'>{bo.bowlingStyle}</div>
                  </div>
                </div>
                <div className='num'>{ballsToOversStr(bo.balls)}</div>
                <div className='num'>{bo.maidens}</div>
                <div className='num'>{bo.runs}</div>
                <div className='num emph'>{bo.wickets}</div>
                <div className='num right'>{economy(bo.runs, bo.balls)}</div>
              </div>
            ))}
          </div>
        </div>
      </SectionCard>

      {Array.isArray(innings.fallOfWickets) && innings.fallOfWickets.length > 0 && (
        <SectionCard className='my-4' title='Fall of Wickets'>
          <ul className='fow'>
            {innings.fallOfWickets.map((f, idx) => (
              <li key={`${f.playerId}-${idx}`} className='fow-item'>
                <img
                  src={`https://media.cricclubs.com${f.playerLogo || ''}`}
                  alt={f.playerName}
                  className='avatar'
                  onError={e => (e.currentTarget.style.visibility = 'hidden')}
                />
                <span className='name'>{f.playerName}</span>
                <span className='sep'>—</span>
                <span className='runs'>{f.total}</span>
              </li>
            ))}
          </ul>
        </SectionCard>
      )}
    </div>
  )
}
